/**
 * Morse — „Üben mit: Meine Zeichen | Alle Zeichen“. (08.10.2026)
 *
 * Martin: „Beim Morse-Code gebe ich Hilfen an, aber ich sehe nur die vier aktivierten (Stufe 3 = K M U R), nicht alle.
 * Falls ich alle üben möchte, wäre es cool, wenn ich alle nehmen könnte und nicht nur das neu hinzugefügte.“
 *
 * EIN Baustein für Hören und Geben: die Wahl, ein Satz, was sie bedeutet (bei „Alle Zeichen“ auch, dass sie nicht für den
 * Aufstieg zählt), und ein Satz, warum sie gerade gesperrt ist. Gemerkt wird am Gerät (morse-hilfe.ts, wie die Hilfe-Wahl);
 * die Vorgabe ist „Meine Zeichen“ — ohne Wahl ändert sich nichts. Mitten in einer Runde (läuft oder ruht, in IRGENDEINEM der
 * beiden Reiter) lässt sich nicht umschalten: die Runde hat beim Start festgelegt, womit sie übt und wie sie bucht.
 *
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-umfang…`).
 */
import { kochZeichen, KOCH_REIHENFOLGE, type Umfang } from "../../shared/morse-koch.js";
import { liesUmfang, merkeUmfang } from "./morse-hilfe.js";
import { UMFANG } from "./morse-texte.js";
import { el, wahl, type MorseUmgebung } from "./morse-teile.js";

let zaehler = 0;

export class MorseUmfang {
  readonly wurzel: HTMLElement;
  private readonly wahl: ReturnType<typeof wahl<Umfang>>;
  private readonly hinweis: HTMLElement;
  private readonly sperre: HTMLElement;

  constructor(
    private readonly u: Pick<MorseUmgebung, "stand" | "rundeOffen">,
    /** Die Wahl hat sich (durch einen Tipp) geändert — der Reiter zeichnet sich neu. */
    private readonly beiWahl: (umfang: Umfang) => void,
  ) {
    const nr = ++zaehler;
    this.wurzel = el("div", "morse-umfang");
    this.wahl = wahl<Umfang>(
      UMFANG.titel,
      (["meine", "alle"] as const).map((id) => ({ id, text: UMFANG.wahl[id] })),
      (id) => {
        // Mitten in einer Runde nicht (die Knöpfe sind dann aus; das hier fängt nur einen verspäteten Tipp ab).
        if (this.u.rundeOffen?.() === true) return;
        merkeUmfang(id);
        this.beiWahl(id);
      },
    );
    this.wahl.wurzel.classList.add("morse-wahl--voll");
    this.hinweis = el("p", "morse-hinweis");
    this.sperre = el("p", "morse-hinweis morse-umfang-sperre", UMFANG.sperre);
    this.sperre.id = `morse-umfang-sperre-${nr}`;
    this.sperre.hidden = true;
    this.wurzel.append(el("p", "morse-feld-titel", UMFANG.titel), this.wahl.wurzel, this.hinweis, this.sperre);
  }

  /** Was gerade gilt (gemerkt am Gerät). */
  umfang(): Umfang {
    return liesUmfang();
  }

  /** Neu lesen und zeigen: die Wahl, der Satz dazu, die Sperre. Gibt zurück, was gilt. */
  zeichne(): Umfang {
    const umfang = liesUmfang();
    const gesperrt = this.u.rundeOffen?.() === true;
    this.wahl.setze(umfang, undefined, gesperrt);
    this.hinweis.textContent = UMFANG.hinweis[umfang](umfang === "alle" ? KOCH_REIHENFOLGE.length : kochZeichen(this.u.stand().stufe).length);
    this.sperre.hidden = !gesperrt;
    for (const b of this.wahl.wurzel.querySelectorAll("button")) {
      if (gesperrt) b.setAttribute("aria-describedby", this.sperre.id);
      else b.removeAttribute("aria-describedby");
    }
    return umfang;
  }
}
